import { useCallback, useEffect, useState } from 'react';
import { labHostsAPI } from '../services/api';
import { ThemeButton } from './ThemeWrapper';

const LabHostsPanel = () => {
  const [hosts, setHosts] = useState([]);
  const [fqdn, setFqdn] = useState('');
  const [alias, setAlias] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await labHostsAPI.listAll();
    setHosts(res.data.hosts || []);
  }, []);

  useEffect(() => {
    load().catch((err) => setError(err.response?.data?.message || 'Could not load lab hosts'));
  }, [load]);

  const addHost = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await labHostsAPI.create({ fqdn, alias });
      setFqdn('');
      setAlias('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not add this host');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (host, isActive) => {
    setError('');
    try {
      await labHostsAPI.update(host.id, { isActive });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update this host');
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold theme-text">Lab hosts for this organization</h2>
      <p className="text-sm theme-text-secondary">
        Create, disable, enable, and delete run only on hosts listed here. Hidden hosts stay in the catalog but drop out of the FQDN picker.
      </p>
      {error ? <p className="text-sm" style={{ color: '#b91c1c' }}>{error}</p> : null}
      <form className="flex flex-wrap gap-2 items-end" onSubmit={addHost}>
        <label className="text-sm theme-text">
          Alias
          <input
            className="block mt-1 px-2 py-2 rounded border theme-text"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="ws2"
          />
        </label>
        <label className="text-sm theme-text">
          FQDN
          <input
            className="block mt-1 px-2 py-2 rounded border theme-text min-w-[280px]"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
            value={fqdn}
            onChange={(event) => setFqdn(event.target.value)}
            placeholder="host.example.com:22"
            required
          />
        </label>
        <ThemeButton type="submit" disabled={saving}>
          Add host
        </ThemeButton>
      </form>
      <ul className="text-sm space-y-1">
        {hosts.map((host) => (
          <li key={host.id} className="flex flex-wrap items-center gap-3">
            <span className="font-mono">{host.fqdn}</span>
            <span className="theme-text-secondary">{host.isActive ? 'visible' : 'hidden'}</span>
            <button type="button" className="underline" onClick={() => setActive(host, !host.isActive)}>
              {host.isActive ? 'Hide' : 'Show'}
            </button>
          </li>
        ))}
        {!hosts.length ? <li className="theme-text-secondary">No lab hosts yet.</li> : null}
      </ul>
    </div>
  );
};

export default LabHostsPanel;
